// SPDX-License-Identifier: MIT
// KeplerIngestAdapter - bridges downloaded blobs into Kepler.gl (Phase 4 scaffold)

import { DownloadResultSuccess } from './types';
import { loadFiles, wrapTo } from '@kepler.gl/actions';
import type { Dispatch } from 'redux';
import { setAzureProviderDataLoaded } from '../azure-slice';
import { classifyExtension, assessItemCounts, POLICY } from '../utils/policy';

function guessType(name: string): 'csv' | 'json' | 'geojson' | 'parquet' | 'unknown' {
  const lower = name.toLowerCase();
  if (lower.endsWith('.geojson')) return 'geojson';
  if (lower.endsWith('.json')) return 'json';
  if (lower.endsWith('.csv')) return 'csv';
  // Treat GeoParquet and common parquet aliases as parquet
  if (
    lower.endsWith('.geoparquet') ||
    lower.endsWith('.parquet') ||
    lower.endsWith('.pq') ||
    lower.endsWith('.parq')
  )
    return 'parquet';
  return 'unknown';
}

export interface IngestResult {
  datasetId: string;
  success: boolean;
  error?: string;
}
interface IngestOptions {
  onWarn?: (msg: string) => void;
  onError?: (msg: string) => void;
}

export class KeplerIngestAdapter {
  constructor(
    private dispatch: Dispatch,
    private mapId = 'atlas-viewer',
    private opts: IngestOptions = {}
  ) {}

  async ingest(results: DownloadResultSuccess[]): Promise<IngestResult[]> {
    const files: File[] = [];
    const out: IngestResult[] = [];
    let runningEstimatedItems = 0;
    for (const r of results) {
      try {
        const kind = guessType(r.path);
        const rawName = r.path.split('/').pop() || r.path;
        // Normalize GeoParquet extension to .parquet so loaders.gl detects it
        let normalizedName = rawName.toLowerCase().endsWith('.geoparquet')
          ? rawName.replace(/\.geoparquet$/i, '.parquet')
          : rawName;
        // If original looked like parquet but mock delivered CSV, preserve usable CSV mime
        const isParquetLogical = kind === 'parquet';
        const deliveredCsv = r.blob.type === 'text/csv';
        const mimeFallback =
          isParquetLogical && deliveredCsv ? 'text/csv' : r.blob.type || 'application/octet-stream';
        // If this is a mocked parquet (CSV masquerading) rename extension to .csv to ensure correct loader selection
        if (isParquetLogical && deliveredCsv && normalizedName.toLowerCase().endsWith('.parquet')) {
          normalizedName = normalizedName.replace(/\.parquet$/i, '.csv');
        }
        // Estimate item counts (CSV & GeoJSON/JSON) for gating
        let estimatedItems: number | undefined;
        try {
          const extClass = classifyExtension(normalizedName);
          if (extClass === 'csv') {
            const SAMPLE = 1_000_000; // first ~1MB
            const sampleBlob = r.blob.slice(0, Math.min(r.blob.size, SAMPLE));
            const text = await sampleBlob.text();
            const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
            if (lines.length > 1) {
              const header = lines[0];
              const dataLines = lines.slice(1);
              const avgLen = dataLines.reduce((s, ln) => s + ln.length, 0) / dataLines.length;
              if (avgLen > 0) {
                estimatedItems = Math.round((r.blob.size - header.length) / avgLen);
              }
            }
          } else if (extClass === 'geojson' || extClass === 'json') {
            if (r.blob.size < POLICY.FILE_SIZE_MB.GEOJSON.HARD * 1024 * 1024) {
              const text = await r.blob.text();
              try {
                const parsed = JSON.parse(text);
                if (
                  parsed &&
                  parsed.type === 'FeatureCollection' &&
                  Array.isArray(parsed.features)
                ) {
                  estimatedItems = parsed.features.length;
                }
              } catch {
                /* ignore parse errors */
              }
            }
          }
        } catch (estErr) {
          this.opts.onWarn?.(`Estimation failed for ${rawName}: ${(estErr as Error).message}`);
        }
        if (typeof estimatedItems === 'number') {
          const assess = assessItemCounts(estimatedItems, runningEstimatedItems);
          if (!assess.allowed) {
            this.opts.onError?.(`${rawName} blocked: ${assess.reason}`);
            out.push({ datasetId: r.path, success: false, error: assess.reason });
            continue; // skip ingest
          } else if (assess.severity === 'warning' && assess.reason) {
            this.opts.onWarn?.(`${rawName}: ${assess.reason}`);
          }
          runningEstimatedItems += estimatedItems;
        }
        const file = new File([r.blob], normalizedName, { type: mimeFallback });
        files.push(file);
        out.push({ datasetId: r.path, success: true });
      } catch (e) {
        out.push({ datasetId: r.path, success: false, error: (e as Error).message });
      }
    }
    if (files.length) {
      this.dispatch(setAzureProviderDataLoaded() as any);
      this.dispatch(wrapTo(this.mapId, loadFiles(files)) as any);
      try {
        if (typeof window !== 'undefined') {
          const w = window as any;
          const existing: string[] = Array.isArray(w.__E2E_DATASETS) ? w.__E2E_DATASETS : [];
          const toAdd = out.filter(r => r.success).map(r => r.datasetId);
          const merged = Array.from(new Set([...existing, ...toAdd]));
          w.__E2E_DATASETS = merged;
        }
      } catch {
        /* non-fatal */
      }
    }
    return out;
  }
}
